import { equals } from "ramda";

const INFINITE_HITS_SESSION_NAME = "ss.infiniteHits";

const cache = {
  write({ cachedState, hits, $bugsnag }) {
    try {
      if (typeof window === "undefined") return;
      globalThis.sessionStorage?.setItem(
        INFINITE_HITS_SESSION_NAME,
        JSON.stringify({ cachedState, hits })
      );
    } catch (err) {
      console.error(err);
      $bugsnag.notify(err);
    }
  },
  read({ cachedState }) {
    try {
      if (typeof window === "undefined") return [];
      const cachedData = globalThis.sessionStorage?.getItem(
        INFINITE_HITS_SESSION_NAME
      );
      const data = cachedData ? JSON.parse(cachedData) : {};
      return equals(cachedState, data.cachedState) ? data.hits : [];
    } catch (err) {
      return [];
    }
  },
};

// based on cache
// function getFirstRecievedPage(cachedState, page = 1) {
//   const cachedHits = cache.read({ cachedState }) ?? {};
//   const pages = Object.keys(cachedHits).map(Number);

//   return pages.length === 0 ? page : Math.min(page, ...pages) + 1;
// }

// New helper for previous pagination
function getMinimumPage(infiniteHits) {
  const pages = Object.keys(infiniteHits ?? {}).map(Number);
  return pages.length === 0 ? null : Math.min(...pages);
}

// Helper to build cache key with correct page param
function buildCacheKey(state) {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("page", state.page ?? 1);
    // normalize '+' to '%20'
    return url.toString().replace(/\+/g, "%20");
  } catch (e) {
    return typeof window !== "undefined"
      ? window.location.href.replace(/\+/g, "%20")
      : "";
  }
}

export default {
  namespaced: true,

  state: () => ({
    started: false,
    loading: false,
    currentPageResults: null,
    infiniteHits: null,
    // Beacon v2 (search module) relies on Search API `responseId`
    responseId: null,
    // Prevent duplicate beacon events for same search response/page
    lastBeaconKey: null,
    query: "",
    pageSize: 12,
    page: 1,
    sortingParams: [],
    facetParams: [],
    bgfacetParams: [],
    otherParams: [],
    initialPageResult: null,
    cachedProductsKey: null,
    cachedHits: null,
    cachedPageResult: null,
    restoredFromCache: false,
  }),

  getters: {
    cachedState(state) {
      return { query: state.query, facetParams: state.facetParams };
    },
    // Sorted hits getter
    hits(state) {
      if (!state.infiniteHits) return [];
      const pages = Object.keys(state.infiniteHits)
        .map(Number)
        .sort((a, b) => a - b);
      return pages.flatMap(p => state.infiniteHits[p] || []);
    },

    isFirstPage(state) {
      const lowestPage = getMinimumPage(state.infiniteHits);
      return lowestPage === null ? true : lowestPage === 0;
    },
    isLastPage(state) {
      return state.currentPageResults?.pagination.totalPages <= state.page;
    },

    getFacetValues: state => facetField => {
      // TODO: Implement feature to load more facet values
      const facets = state.currentPageResults?.facets ?? [];
      const facet = facets.find(({ field }) => field === facetField);
      return facet?.values ?? [];
    },
    toBeaconProduct(state, getters) {
      return productId => {
        const pid = String(productId);
        const product =
          (state.currentPageResults?.results ?? []).find(
            p => String(p?.uid ?? p?.id) === pid
          ) ??
          getters.hits.find(p => String(p?.uid ?? p?.id) === pid) ??
          null;

        if (!product) return null;

        const uid = String(product.uid ?? product.id ?? "");
        if (!uid) return null;

        const sku =
          product.sku ??
          product.mappings?.core?.sku ??
          product.mappings?.sku ??
          undefined;

        // For Search API results, parentId is typically the same as uid
        return {
          type: "product",
          parentId: uid,
          uid,
          ...(sku ? { sku: String(sku) } : {}),
        };
      };
    },
  },

  actions: {
    initState(
      { state, commit, dispatch },
      { pageSize = 12, queryParams, bgfacets } = {}
    ) {
      const key =
        typeof window !== "undefined"
          ? window.location.href.replace(/\+/g, "%20")
          : null;

      // If different PLP, reset single-entry cache
      if (state.cachedProductsKey && state.cachedProductsKey !== key) {
        commit("RESET_CACHE");
      }

      commit("SET_STARTED", true);

      commit(
        "SET_FACET_PARAMS",
        queryParams.filter(([key]) => key.startsWith("filter."))
      );

      commit(
        "SET_OTHER_PARAMS",
        queryParams.filter(
          ([key]) => !/(^query$|^filter\.|^page$|^sort\.)/.test(key)
        )
      );

      const query = queryParams.find(([key]) => key === "query")?.[1] ?? "";
      commit("SET_QUERY", { query });

      const page = Number(
        queryParams.find(([key]) => key === "page")?.[1] ?? 1
      );
      commit("SET_PAGE", { page });

      commit("SET_PAGE_SIZE", { pageSize });

      commit("SET_BGFACET_PARAMS", bgfacets);

      const sorting =
        queryParams.find(([key]) => key.startsWith("sort.")) ?? [];

      dispatch(
        "setSortingParams",
        sorting?.length > 0
          ? { field: sorting[0].replace("sort.", ""), direction: sorting[1] }
          : {}
      );
    },
    hydrate({ commit, getters }) {
      commit(
        "SET_INFINITE_HITS",
        cache.read({ cachedState: getters.cachedState })
      );
    },

    async start({ state, commit, dispatch }, payload) {
      // ---------------------------
      // 1. CSR — Already have data (navigating back)
      // ---------------------------
      if (
        process.client &&
        state.restoredFromCache === true &&
        state.infiniteHits &&
        Object.keys(state.infiniteHits).length > 0
      ) {
        return;
      }

      // ---------------------------
      // 2. SSR — Replace everything
      // ---------------------------
      if (typeof window === "undefined") {
        commit("SET_LOADING", true);

        const pages = await this.$productsSearch.start(payload);

        const pageNumbers = Object.keys(pages)
          .map(n => Number(n))
          .sort((a, b) => a - b);

        for (const p of pageNumbers) {
          const data = pages[p];

          if (p === 1) {
            commit("SET_INITIAL_PAGE_RESULT", { data });
          }

          commit("UPDATE_SEARCH_RESULTS", {
            data,
            cachedState: {
              query: state.query,
              facetParams: state.facetParams,
            },
          });
        }

        commit("SET_LOADING", false);
        commit("SET_RESTORED_FROM_CACHE", false);
        return;
      }

      // ---------------------------
      // 3. CSR Reload — Restore from session
      // ---------------------------
      const key = window.location.href.replace(/\+/g, "%20");

      if (
        state.cachedProductsKey === key &&
        state.cachedHits &&
        state.cachedPageResult
      ) {
        commit("RESTORE_INFINITE_HITS", state.cachedHits);
        commit("SET_CURRENT_PAGE_RESULT", { data: state.cachedPageResult });
        commit("SET_STARTED", true);
        commit("SET_RESTORED_FROM_CACHE", true);
        // Fire beacon events for restored state only on client
        await dispatch("trackSearchRenderImpression");
        return;
      }

      // ---------------------------
      // 4. CSR Fresh Load — Merge pages
      // ---------------------------
      commit("SET_LOADING", true);

      const pages = await this.$productsSearch.start(payload);

      const pageNumbersCSR = Object.keys(pages)
        .map(n => Number(n))
        .sort((a, b) => a - b);

      for (const p of pageNumbersCSR) {
        const data = pages[p];

        if (p === 1) {
          commit("SET_INITIAL_PAGE_RESULT", { data });
        }

        commit("UPDATE_SEARCH_RESULTS", {
          data,
          cachedState: {
            query: state.query,
            facetParams: state.facetParams,
          },
        });
      }

      commit("SET_LOADING", false);

      const lastPageData = pages[pageNumbersCSR[pageNumbersCSR.length - 1]];
      commit("SET_CACHE", {
        key,
        hits: state.infiniteHits,
        pageResult: lastPageData,
      });

      commit("SET_RESTORED_FROM_CACHE", false);
      await dispatch("trackSearchRenderImpression");
    },

    async refine({ commit, getters, state, dispatch }, { widget, facet }) {
      if (!state.started) throw new Error("First call start method first.");

      const { data } = await this.$productsSearch.refine(widget, facet);

      if (widget === "CLEAR_REFINEMENT") {
        commit("SET_INITIAL_PAGE_RESULT", { data });
      }

      commit("SET_SEARCH_RESULTS", { data, cachedState: getters.cachedState });
      commit("SET_RESTORED_FROM_CACHE", false);
      await dispatch("trackSearchRenderImpression");
    },
    async sort({ commit, getters, state, dispatch }, sortingOption) {
      if (!state.started) throw new Error("First call start method first.");

      const { data } = await this.$productsSearch.sort(sortingOption);
      commit("SET_SEARCH_RESULTS", { data, cachedState: getters.cachedState });
      commit("SET_RESTORED_FROM_CACHE", false);
      await dispatch("trackSearchRenderImpression");
    },
    async showMore({ state, commit, getters, dispatch }) {
      if (!state.started) throw new Error("First call start method first.");

      const page = Number(state.page) + 1;
      const { data } = await this.$productsSearch.showMore(page);

      commit("UPDATE_SEARCH_RESULTS", {
        data,
        cachedState: getters.cachedState,
      });

      // Update cache for current URL
      const key = buildCacheKey(state);
      if (key) {
        commit("SET_CACHE", {
          key,
          hits: state.infiniteHits,
          pageResult: data,
        });
      }

      commit("SET_RESTORED_FROM_CACHE", false);
      await dispatch("trackSearchRenderImpression");
    },
    async showPrevious({ state, commit, getters, dispatch }) {
      if (!state.started) throw new Error("First call start method first.");

      const lowestPage = getMinimumPage(state.infiniteHits);
      if (lowestPage === null || lowestPage === 0) return;

      const { data } = await this.$productsSearch.showPrevious(lowestPage);

      commit("UPDATE_SEARCH_RESULTS", {
        data,
        cachedState: getters.cachedState,
      });

      //  Update cache for current URL
      const key = buildCacheKey(state);
      if (key) {
        commit("SET_CACHE", {
          key,
          hits: state.infiniteHits,
          pageResult: data,
        });
      }

      commit("SET_RESTORED_FROM_CACHE", false);
      await dispatch("trackSearchRenderImpression");
    },
    async trackSearchRenderImpression({ state, getters, commit }) {
      if (process.server) return;

      const responseId =
        state.responseId ?? state.currentPageResults?.responseId;
      if (!responseId) return;

      const page =
        state.currentPageResults?.pagination?.currentPage ?? state.page;
      const beaconKey = `${responseId}:${page}`;
      if (state.lastBeaconKey === beaconKey) return;

      const results = (state.currentPageResults?.results ?? [])
        .map(r => getters.toBeaconProduct(r?.uid ?? r?.id))
        .filter(Boolean);

      // Render is required for Search reporting; impression improves analytics accuracy.
      await this.$search.callBeacon2Api("search", "render", {
        responseId: String(responseId),
      });

      if (results.length > 0) {
        await this.$search.callBeacon2Api("search", "impression", {
          responseId: String(responseId),
          results,
          banners: [],
        });
      }

      commit("SET_LAST_BEACON_KEY", { key: beaconKey });
    },
    async trackSearchClickthrough({ state, getters }, { productId }) {
      if (process.server) return;

      const responseId =
        state.responseId ?? state.currentPageResults?.responseId;
      if (!responseId) return;

      const result = getters.toBeaconProduct(productId);
      if (!result) return;

      await this.$search.callBeacon2Api("search", "clickthrough", {
        responseId: String(responseId),
        results: [result],
      });
    },
    updateURLQuery({ state }) {
      const searchParams = new URLSearchParams(state.facetParams);
      const queryParams = [...searchParams.keys()].map(key => {
        const values = searchParams.getAll(key);
        return [key, values.length > 1 ? values : values[0]];
      });

      this.$router.push({
        query: {
          ...(state.query && { query: state.query }),
          page: state.page,
          ...Object.fromEntries(state.sortingParams),
          ...Object.fromEntries(state.otherParams),
          ...Object.fromEntries(queryParams),
        },
      });
    },
    setFacetParams({ commit }, { facetParams }) {
      commit("SET_FACET_PARAMS", facetParams);
    },
    setPage({ commit }, { page }) {
      commit("SET_PAGE", { page });
    },
    setSortingParams({ commit }, { field, direction }) {
      const sortingParams =
        field && direction ? [[`sort.${field}`, direction]] : [];
      commit("SET_SORTING_PARAMS", sortingParams);
    },
    dispose({ commit }) {
      commit("SET_STARTED", false);
      commit("SET_LOADING", false);
      commit("SET_RESTORED_FROM_CACHE", false);
      commit("SET_CURRENT_PAGE_RESULT", { data: null });
      commit("SET_INFINITE_HITS", {});
    },
    setLoading({ commit }, flag) {
      commit("SET_LOADING", flag);
    },
  },

  mutations: {
    SET_SEARCH_RESULTS(state, { data, cachedState }) {
      if (!data) {
        state.currentPageResults = null;
        state.infiniteHits = null;
        state.responseId = null;
      } else {
        const page = data.pagination?.currentPage ?? 1;
        state.infiniteHits = { [page - 1]: data.results };
        state.currentPageResults = data;
        state.responseId = data.responseId ?? null;
      }

      cache.write({
        cachedState,
        hits: state.infiniteHits,
        $bugsnag: this.$bugsnag,
      });
    },
    UPDATE_SEARCH_RESULTS(state, { data, cachedState }) {
      const page = data.pagination?.currentPage ?? 1;
      const updated = { ...(state.infiniteHits || {}) };
      updated[page - 1] = data.results;
      state.infiniteHits = updated;
      state.currentPageResults = data;
      state.responseId = data.responseId ?? null;

      cache.write({
        cachedState,
        hits: state.infiniteHits,
        $bugsnag: this.$bugsnag,
      });
    },
    SET_INITIAL_PAGE_RESULT(state, { data }) {
      state.initialPageResult = data;
    },

    SET_INFINITE_HITS(state, hits = {}) {
      state.infiniteHits = { ...hits };
    },
    RESTORE_INFINITE_HITS(state, hits = {}) {
      state.infiniteHits = { ...hits };
    },
    SET_CURRENT_PAGE_RESULT(state, { data }) {
      state.currentPageResults = data;
      state.responseId = data?.responseId ?? null;
    },
    SET_LAST_BEACON_KEY(state, { key }) {
      state.lastBeaconKey = key;
    },
    SET_PAGE(state, { page }) {
      state.page = Number(page);
    },
    SET_PAGE_SIZE(state, { pageSize }) {
      state.pageSize = pageSize;
    },
    SET_QUERY(state, { query }) {
      state.query = query;
    },
    SET_FACET_PARAMS(state, params) {
      state.facetParams = params;
    },
    SET_OTHER_PARAMS(state, params) {
      state.otherParams = params;
    },
    SET_BGFACET_PARAMS(state, params) {
      state.bgfacetParams = params;
    },
    SET_STARTED(state, flag) {
      state.started = flag;
    },
    SET_SORTING_PARAMS(state, sortingParams) {
      state.sortingParams = sortingParams;
    },
    SET_LOADING(state, flag) {
      state.loading = flag;
    },
    SET_CACHE(state, { key, hits, pageResult }) {
      state.cachedProductsKey = key;
      state.cachedHits = hits;
      state.cachedPageResult = pageResult;
    },
    RESET_CACHE(state) {
      state.cachedProductsKey = null;
      state.cachedHits = null;
      state.cachedPageResult = null;
    },
    SET_RESTORED_FROM_CACHE(state, value) {
      state.restoredFromCache = value;
    },
  },
};
